import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useQuiz } from '../context/QuizContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiArrowLeft } = FiIcons;

const QuizScreen = () => {
  const navigate = useNavigate();
  const { questions, currentQuestion, setCurrentQuestion, answers, saveAnswer, setQuizCompleted, userInfo } = useQuiz();

  const handleAnswerSelect = (selectedOption) => {
    saveAnswer(questions[currentQuestion].id, selectedOption);
    setTimeout(() => {
      if (currentQuestion < questions.length - 1) {
        setCurrentQuestion(currentQuestion + 1);
      } else {
        setQuizCompleted(true);
        navigate('/result');
      }
    }, 400);
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) setCurrentQuestion(currentQuestion - 1);
  };

  const progress = ((currentQuestion + 1) / questions.length) * 100;
  const currentAnswer = answers[questions[currentQuestion]?.id];

  return (
    <div className="min-h-screen flex items-center justify-center p-4 font-sans">
      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="max-w-2xl w-full bg-white rounded-3xl shadow-2xl overflow-hidden"
      >
        {/* Header Area */}
        <div className="bg-brand-blue p-6 md:p-8 text-white">
          <div className="flex justify-between items-center mb-6">
            <button 
              onClick={handlePrevious} 
              disabled={currentQuestion === 0}
              className={`p-2 rounded-full transition-colors ${currentQuestion === 0 ? 'opacity-20' : 'hover:bg-white/10'}`}
            >
              <SafeIcon icon={FiArrowLeft} className="text-xl" />
            </button>
            <div className="text-center">
              <span className="font-display text-2xl tracking-wider text-brand-red">
                {currentQuestion + 1} / {questions.length}
              </span>
            </div>
            <div className="w-10"></div>
          </div>
          
          {/* Progress Section */}
          <div className="space-y-3">
            <div className="flex justify-between items-end">
              <span className="text-xs font-bold uppercase tracking-widest opacity-70">Dein Fortschritt</span>
              <span className="text-xs font-bold uppercase tracking-widest text-brand-red">Mach das Quiz und du hast Klarheit</span>
            </div>
            <div className="w-full bg-white/10 rounded-full h-3 overflow-hidden border border-white/5">
              <motion.div 
                className="bg-brand-red h-full"
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.5 }}
              />
            </div>
          </div>
        </div>

        {/* Question Area */}
        <div className="p-8 md:p-12">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentQuestion}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="min-h-[300px] flex flex-col justify-center"
            >
              <h2 className="text-2xl md:text-3xl font-display text-brand-blue mb-10 text-center leading-tight">
                {questions[currentQuestion]?.question}
              </h2>

              <div className="grid gap-3">
                {questions[currentQuestion]?.options.map((option, index) => (
                  <button
                    key={index}
                    onClick={() => handleAnswerSelect(option)}
                    className={`w-full p-5 text-left rounded-xl border-2 transition-all duration-200 group
                      ${currentAnswer?.text === option.text 
                        ? 'border-brand-blue bg-brand-blue text-white shadow-lg' 
                        : 'border-gray-100 hover:border-brand-blue/30 hover:bg-brand-gray text-gray-700'
                      }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-lg leading-snug">{option.text}</span>
                      <div className={`w-5 h-5 rounded-full border-2 flex-shrink-0 ml-4
                        ${currentAnswer?.text === option.text ? 'border-brand-red bg-brand-red shadow-inner' : 'border-gray-300'}`} 
                      />
                    </div>
                  </button>
                ))}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
};

export default QuizScreen;